import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import './Navbar.css';
import { UserIcon, SettingsIcon } from './Icons';
import { API, getAuth, clearAuth } from '../api';
import {
  AVATAR_OPTIONS,
  loadProfileExtras,
  saveProfileExtras,
  subscribeToProfileExtrasUpdates,
} from '../utils/profileExtras';

function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profileAppearance, setProfileAppearance] = useState({ profilePhoto: '', avatarId: 'letter' });
  const [userInitial, setUserInitial] = useState('');
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Close dropdown if clicked outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    let cancelled = false;

    const syncFromLocal = () => {
      const auth = getAuth();
      const extras = loadProfileExtras(auth?.userId);
      if (!cancelled) {
        setProfileAppearance({
          profilePhoto: extras.profilePhoto || '',
          avatarId: extras.avatarId || 'letter',
        });
      }
    };

    const loadProfileAppearance = async () => {
      const auth = getAuth();
      if (!auth?.token || !auth?.userId) {
        syncFromLocal();
        return;
      }

      const localExtras = loadProfileExtras(auth.userId);
      if (!cancelled) {
        setProfileAppearance({
          profilePhoto: localExtras.profilePhoto || '',
          avatarId: localExtras.avatarId || 'letter',
        });
      }

      try {
        const response = await fetch(API.userById(auth.userId), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
          },
          credentials: 'include',
        });
        if (!response.ok) return;

        const profile = await response.json();
        const nextAppearance = {
          profilePhoto: profile.profilePhoto || localExtras.profilePhoto || '',
          avatarId: profile.avatarId || localExtras.avatarId || 'letter',
        };

        saveProfileExtras(auth.userId, nextAppearance);

        if (!cancelled) {
          setProfileAppearance(nextAppearance);
          // Extract user initial from firstName or lastName
          const initial = (profile.firstName?.[0] || profile.lastName?.[0] || '').toUpperCase();
          setUserInitial(initial);
        }
      } catch (error) {
        console.error('Error loading navbar profile appearance:', error);
      }
    };

    loadProfileAppearance();

    const unsubscribe = subscribeToProfileExtrasUpdates(syncFromLocal);
    window.addEventListener('focus', loadProfileAppearance);

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('focus', loadProfileAppearance);
    };
  }, []);

  const handleLogout = () => {
    clearAuth();
    setDropdownOpen(false);
    navigate('/', { replace: true });
  };

  const avatarOption = AVATAR_OPTIONS.find((option) => option.id === profileAppearance.avatarId);

  return (
    <nav className="navbar">
      <div className="navbar-content">

        {/* LEFT: BRAND LOGO */}
        <div className="navbar-brand" onClick={() => navigate('/dashboard')}>
          <img 
            src={require('../assets/onboarding/938a8ebc-324d-4470-88d9-3ac7c40c90d6.png')} 
            alt="HRV-4" 
            className="brand-logo"
          />
        </div>

        {/* CENTER: NAVIGATION LINKS */}
        <div className="nav-links-center">
          <NavLink to="/dashboard" className="nav-item" end>
            Dashboard
          </NavLink>
          <NavLink to="/graphs" className="nav-item">
            Graphs
          </NavLink>
          <NavLink to="/insights" className="nav-item">
            Insights
          </NavLink>
          <NavLink to="/activities" className="nav-item">
            Activities
          </NavLink>
          <NavLink to="/sensors" className="nav-item">
            My Sensors
          </NavLink>
        </div>

        {/* RIGHT: USER DROPDOWN */}
        <div className="navbar-user" ref={dropdownRef}>
          <div
            className={`user-circle ${dropdownOpen ? 'active' : ''}`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            {profileAppearance.profilePhoto ? (
              <img src={profileAppearance.profilePhoto} alt="Profile" className="user-circle-image" />
            ) : avatarOption?.emoji ? (
              <span className="user-circle-emoji">{avatarOption.emoji}</span>
            ) : userInitial ? (
              <span className="user-circle-initial">{userInitial}</span>
            ) : (
              <UserIcon size={20} color={dropdownOpen ? "#007AFF" : "#555"} />
            )}
          </div>

          {dropdownOpen && (
            <div className="user-dropdown">
              <div className="dropdown-arrow"></div>

              <div
                className="dropdown-item"
                onClick={() => { navigate('/profile'); setDropdownOpen(false); }}
              >
                <SettingsIcon size={16} color="#555" />
                <span>Profile Settings</span>
              </div>

              <div className="dropdown-divider"></div>

              <div className="dropdown-item logout-item" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                <span>Log Out</span>
              </div>
            </div>
          )}
        </div>

      </div>
    </nav>
  );
}

export default Navbar;